(function () {
  if (customElements.get('ei-sticky-atc')) return;

  const MOBILE = '(max-width: 767px)';
  const PRODUCT_SECTION = 'section[data-section-type="product"]';
  const FOCUSABLE = 'button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const BODY_BAR = 'ei-satc-bar-visible';
  const BODY_SHEET = 'ei-satc-sheet-open';

  // Body classes are shared by every instance; a class clears only when no instance needs it.
  const owners = {[BODY_BAR]: new Set(), [BODY_SHEET]: new Set()};
  const own = (name, instance, on) => {
    owners[name][on ? 'add' : 'delete'](instance);
    document.body.classList.toggle(name, owners[name].size > 0);
  };

  const later = (timers, fn, ms) => {
    const id = setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
    return id;
  };

  /* =====================================================================
     Sheet controller: open, close, drag, focus, purchase toggle, quantity,
     CTA states. Product data comes from its data attributes.
     Events: ei-sheet:open, ei-sheet:close, ei-sheet:change
     ===================================================================== */

  class EiAtcSheet extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'single';
      this.sheet = this.querySelector('[data-sheet]');
      this.overlay = this.querySelector('[data-overlay]');
      this.variantId = this.dataset.variantId;
      this.planIds = (this.dataset.planIds || '').split(',').filter(Boolean);
      this.planPercents = {};
      (this.dataset.planPercents || '').split(',').filter(Boolean).forEach((pair) => {
        const [id, pct] = pair.split(':');
        this.planPercents[id] = pct;
      });
      this.defaultPlan = this.dataset.planId || '';
      this.plan = '';
      this.hasPlans = this.dataset.hasPlans === 'true';
      this.requiresPlan = this.dataset.requiresPlan === 'true';
      this.qtyMax = parseInt(this.dataset.qtyMax, 10) || 0;
      this.qty = 1;
      this.purchase = this.dataset.purchase || 'onetime';
      this.touched = false;
      this.isOpen = false;
      this.adding = false;
      this.verifying = false;
      this.locked = false;
      this.timers = new Set();

      this.onKey = (event) => this.handleKey(event);
      this.ui = new AbortController();
      this.bindUi(this.ui.signal);
      this.updateSavings();
    }

    disconnectedCallback() {
      this.ui.abort();
      this.teardown();
      this.timers.forEach((id) => clearTimeout(id));
      this.timers.clear();
    }

    get savePercent() {
      return this.planPercents[this.plan || this.defaultPlan];
    }

    teardown() {
      if (this.cancelDrag) this.cancelDrag();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.isOpen || this.locked) this.close({skipFocus: true});
      own(BODY_SHEET, this, false);
    }

    /* ---------- purchase option ---------- */

    setPurchase(value) {
      if (!this.hasPlans) value = 'onetime';
      if (this.requiresPlan) value = 'subscription';
      this.purchase = value;
      this.dataset.purchase = value;
      this.querySelectorAll('[data-option]').forEach((seg) => {
        seg.setAttribute('aria-checked', String(seg.dataset.option === value));
      });
      this.dispatchEvent(new CustomEvent('ei-sheet:change', {bubbles: true}));
    }

    // Remember the exact plan the shopper has selected.
    setPlan(id) {
      if (!this.planIds.includes(id)) return;
      this.plan = id;
      this.updateSavings();
      this.dispatchEvent(new CustomEvent('ei-sheet:change', {bubbles: true}));
    }

    updateSavings() {
      const percent = this.savePercent;
      if (percent === undefined) return;
      this.querySelectorAll('[data-save]').forEach((el) => {
        el.textContent = (this.dataset.saveTemplate || '').replace('%%P%%', percent);
      });
    }

    wantedPlan() {
      return this.plan || this.defaultPlan;
    }

    get validTier() {
      const card = this.selectedTier;
      return card && this.contains(card) && !card.disabled && card.dataset.available === 'true' &&
        /^\d+$/.test(card.dataset.variantId || '') && /^\d+$/.test(card.dataset.planId || '');
    }

    get tierFrozen() {
      return this.busy || this.verifying || window.eiCartAdd.unconfirmed(this.variantId) ||
        this.cta.dataset.state === 'success';
    }

    selectTier(card) {
      if (this.tierFrozen || !card || card.disabled || card.dataset.available !== 'true' || !card.dataset.planId) return;
      this.selectedTier = card;
      this.variantId = card.dataset.variantId;
      this.tiers.forEach((tier) => {
        const selected = tier === card;
        tier.setAttribute('aria-checked', String(selected));
        tier.tabIndex = selected ? 0 : -1;
      });
      this.querySelector('.ei-atc-sheet__cta-label--idle').textContent =
        this.dataset.subscribeTemplate.replace('%%PRICE%%', card.dataset.price);
      this.setError('');
      this.setCta('idle');
    }

    seedTier(id) {
      if (this.tierFrozen) return;
      if (!this.tierSeeded) {
        this.tierSeeded = true;
        const seed = this.tiers.find((card) => card.dataset.variantId === id && !card.disabled);
        this.selectTier(seed || this.tiers.find((card) => !card.disabled));
      } else if (!this.validTier) {
        this.selectTier(this.tiers.find((card) => !card.disabled));
      }
    }

    bindTiers(signal) {
      this.tiers = [...this.querySelectorAll('[data-tier]')];
      this.tiers.forEach((card) => {
        card.addEventListener('click', () => this.selectTier(card), {signal});
        card.addEventListener('keydown', (event) => {
          if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          if (this.tierFrozen) return;
          const cards = this.tiers.filter((tier) => !tier.disabled && tier.dataset.available === 'true' && tier.dataset.planId);
          const step = ['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1;
          const next = cards[(cards.indexOf(card) + step + cards.length) % cards.length];
          if (next) {
            this.selectTier(next);
            next.focus();
          }
        }, {signal});
      });
      this.selectTier(this.tiers.find((card) => !card.disabled));
    }

    /* ---------- quantity ---------- */

    setQty(value) {
      let qty = Math.max(1, parseInt(value, 10) || 1);
      if (this.qtyMax) qty = Math.min(qty, this.qtyMax);
      this.qty = qty;
      const out = this.querySelector('[data-qty-value]');
      if (out) out.textContent = String(qty);
      const minus = this.querySelector('[data-qty-minus]');
      const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.disabled = qty <= 1;
      if (plus) plus.disabled = !!this.qtyMax && qty >= this.qtyMax;
    }

    /* ---------- UI wiring ---------- */

    get busy() {
      return this.adding || window.eiCartAdd.pending(this.variantId);
    }

    bindUi(signal) {
      this.querySelector('[data-close]').addEventListener('click', () => this.close(), {signal});
      this.overlay.addEventListener('click', () => this.close(), {signal});

      this.querySelectorAll('[data-option]').forEach((seg) => {
        seg.addEventListener(
          'click',
          () => {
            if (this.busy) return;
            this.touched = true;
            this.setPurchase(seg.dataset.option);
          },
          {signal}
        );
      });

      if (this.mode === 'monthly') {
        this.bindTiers(signal);
      } else {
        const step = (delta) => {
          if (!this.busy) this.setQty(this.qty + delta);
        };
        this.querySelector('[data-qty-minus]').addEventListener('click', () => step(-1), {signal});
        this.querySelector('[data-qty-plus]').addEventListener('click', () => step(1), {signal});
      }
      this.querySelector('[data-add]').addEventListener('click', () => this.add(), {signal});

      this.bindDrag(this.querySelector('[data-drag]'), signal);
      if (this.mode !== 'monthly') {
        this.setQty(1);
        this.setPurchase(this.purchase);
      }
    }

    /* ---------- open / close ---------- */

    open(opener) {
      if (this.isOpen) return;
      this.renderShips();
      this.setError('');
      clearTimeout(this.resetTimer);
      this.setCta(this.busy ? 'loading' : 'idle');
      this.opener = opener || null;
      this.isOpen = true;
      if (window.eiCartAdd.unconfirmed(this.variantId)) this.verifyBlocked();

      this.sheet.inert = false;
      this.sheet.removeAttribute('aria-hidden');
      this.classList.add('is-open');
      own(BODY_SHEET, this, true);
      this.lockScroll();
      document.addEventListener('keydown', this.onKey);
      this.raf = requestAnimationFrame(() => {
        const target = this.mode === 'monthly' && this.validTier ? this.selectedTier : this.sheet;
        target.focus({preventScroll: true});
      });
      this.dispatchEvent(new CustomEvent('ei-sheet:open', {bubbles: true}));
    }

    close(options = {}) {
      this.releaseHold();
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      own(BODY_SHEET, this, false);
      this.sheet.inert = true;
      this.sheet.setAttribute('aria-hidden', 'true');
      this.dispatchEvent(new CustomEvent('ei-sheet:close', {bubbles: true}));

      if (!options.skipFocus) this.restoreFocus();
      this.opener = null;
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => this.resetCta(), 400);
    }

    releaseHold() {
      document.removeEventListener('keydown', this.onKey);
      this.unlockScroll();
    }

    restoreFocus() {
      const el = this.opener;
      if (!el || !el.isConnected || !el.getClientRects().length) return;
      if (getComputedStyle(el).visibility === 'hidden') return;
      el.focus({preventScroll: true});
    }

    lockScroll() {
      if (this.locked) return;
      this.locked = true;
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.sheet}));
    }

    unlockScroll() {
      if (!this.locked) return;
      this.locked = false;
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }

    handleKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...this.sheet.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.sheet)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- drag to dismiss ---------- */

    bindDrag(handle, signal) {
      let startY = 0;
      let lastY = 0;
      let lastT = 0;
      let velocity = 0;
      let dragging = false;
      let active = false;

      const clear = () => {
        this.classList.remove('is-dragging');
        this.sheet.style.transform = '';
        this.overlay.style.opacity = '';
      };

      this.cancelDrag = () => {
        active = false;
        dragging = false;
        clear();
      };

      const apply = (dy) => {
        const height = this.sheet.offsetHeight || 1;
        const offset = dy < 0 ? -Math.min(28, Math.sqrt(-dy) * 3) : dy;
        this.sheet.style.transform = `translateY(${offset}px)`;
        this.overlay.style.opacity = String(1 - Math.min(1, Math.max(dy, 0) / height));
      };

      handle.addEventListener(
        'pointerdown',
        (event) => {
          if (!this.isOpen || event.target.closest('[data-close]')) return;
          active = true;
          dragging = false;
          startY = lastY = event.clientY;
          lastT = event.timeStamp;
          velocity = 0;
        },
        {signal}
      );

      handle.addEventListener(
        'pointermove',
        (event) => {
          if (!active) return;
          const dy = event.clientY - startY;
          if (!dragging) {
            if (Math.abs(dy) < 4) return;
            dragging = true;
            this.classList.add('is-dragging');
            handle.setPointerCapture(event.pointerId);
          }
          const dt = event.timeStamp - lastT;
          if (dt > 0) velocity = (event.clientY - lastY) / dt;
          lastY = event.clientY;
          lastT = event.timeStamp;
          apply(dy);
        },
        {signal}
      );

      const end = (event) => {
        if (!active) return;
        active = false;
        if (!dragging) return;
        dragging = false;
        const dy = event.clientY - startY;
        const height = this.sheet.offsetHeight || 1;
        const shouldClose = event.type !== 'pointercancel' && (dy > height * 0.3 || velocity > 0.5);

        this.classList.remove('is-dragging');
        void this.sheet.offsetHeight;
        if (shouldClose) this.close();
        this.sheet.style.transform = '';
        this.overlay.style.opacity = '';
      };

      handle.addEventListener('pointerup', end, {signal});
      handle.addEventListener('pointercancel', end, {signal});
    }

    /* ---------- add to cart ---------- */

    get cta() {
      return this.querySelector('[data-add]');
    }

    setCta(state) {
      const cta = this.cta;
      cta.dataset.state = state;
      cta.disabled = state === 'loading' || state === 'success';
      if (state === 'loading') cta.setAttribute('aria-busy', 'true');
      else cta.removeAttribute('aria-busy');
      if (this.mode === 'monthly' && !this.validTier) {
        cta.disabled = true;
        this.setError(this.dataset.planErrorText);
      }
    }

    resetCta() {
      if (!this.isOpen && !this.busy) this.setCta('idle');
    }

    setError(message, tone = 'error', withLink = false) {
      const el = this.querySelector('[data-error]');
      el.textContent = message;
      el.dataset.tone = tone;
      if (withLink && message) {
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = this.dataset.viewCartText;
        el.append(' ', link);
      }
    }

    async add() {
      if (this.busy || !this.isOpen || window.eiCartAdd.unconfirmed(this.variantId)) return;
      if (this.mode === 'monthly' && (this.tierFrozen || !this.validTier)) {
        if (!this.validTier) this.setCta('idle');
        return;
      }
      const item = this.mode === 'monthly'
        ? {id: this.selectedTier.dataset.variantId, quantity: 1, sellingPlan: this.selectedTier.dataset.planId}
        : {id: this.variantId, quantity: this.qty, sellingPlan: this.purchase === 'subscription' ? this.wantedPlan() : ''};
      this.setError('');
      this.setCta('loading');
      this.adding = true;

      const result = await window.eiCartAdd.add(
        item,
        () => this.setError(this.dataset.stillText, 'info')
      );
      this.adding = false;

      if (result.status === 'success') {
        this.broadcast();
        this.succeed();
      }
      else if (result.status === 'unknown' || result.status === 'blocked') this.showBlocked();
      else this.fail(result.message);
    }

    // Let the theme refresh its cart badge and listeners after an add.
    broadcast() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const subs = (window.theme && window.theme.subscribers && window.theme.subscribers['cart-update']) || [];
      subs.slice().forEach((callback) => {
        try {
          callback({source: 'product-form', productVariantId: String(this.variantId)});
        } catch (e) {
          /* ignore subscriber errors */
        }
      });
    }

    fail(message) {
      this.setCta('idle');
      if (!this.isOpen) return;
      this.setError(message || this.dataset.errorText);
      const cta = this.cta;
      cta.classList.remove('is-shaking');
      void cta.offsetWidth;
      cta.classList.add('is-shaking');
      later(this.timers, () => cta.classList.remove('is-shaking'), 450);
    }

    succeed() {
      if (!this.isOpen) {
        this.setCta('idle');
        return;
      }
      this.setCta('success');
      this.setError('');
      this.cta.setAttribute('aria-label', this.querySelector('.ei-atc-sheet__cta-label--success').textContent.trim());
      // The cart opens now: give up focus trap and scroll lock, and skip focus restore.
      this.releaseHold();
      this.opener = null;
      later(
        this.timers,
        () => {
          this.cta.removeAttribute('aria-label');
          this.close({skipFocus: true});
        },
        600
      );
    }

    showBlocked() {
      if (!this.isOpen) return;
      this.setCta('idle');
      this.cta.disabled = true;
      this.setError(this.dataset.unknownText, 'error', true);
    }

    // Reopened after an unknown outcome: adding is allowed again only after a fresh read shows no increase.
    async verifyBlocked() {
      if (this.mode === 'monthly' && this.verifying) {
        this.showBlocked();
        return;
      }
      this.verifying = true;
      this.showBlocked();
      const outcome = await window.eiCartAdd.verify(this.variantId);
      this.verifying = false;
      if (outcome === 'unknown') {
        this.showBlocked();
      } else if (outcome === 'added') {
        // The earlier add did land: treat it as a success, never as a retry.
        this.broadcast();
        this.succeed();
      } else {
        this.setError('');
        if (!this.busy) this.setCta('idle');
      }
    }

    /* ---------- ships by ---------- */

    renderShips() {
      const wrap = this.querySelector('[data-ships]');
      if (!wrap) return;
      const text = wrap.querySelector('[data-ships-text]');
      const [before, after = ''] = (text.dataset.template || '').split('%%DATE%%');
      text.textContent = '';
      text.append(before);
      const strong = document.createElement('strong');
      strong.textContent = this.shipsDate();
      text.append(strong, after);
      wrap.hidden = false;
    }

    shipsDate() {
      const days = parseInt(this.dataset.shipDays, 10) || 0;
      const cutoff = parseInt(this.dataset.cutoffHour, 10);
      const skip = this.dataset.skipWeekends === 'true';
      let zone = this.dataset.timezone || undefined;
      let parts;

      const read = () =>
        new Intl.DateTimeFormat('en-US', {
          timeZone: zone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hourCycle: 'h23',
        }).formatToParts(new Date());

      try {
        parts = read();
      } catch (e) {
        zone = undefined;
        parts = read();
      }

      const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
      let day = Date.UTC(get('year'), get('month') - 1, get('day'));
      const weekend = (ms) => [0, 6].includes(new Date(ms).getUTCDay());
      const DAY = 86400000;

      if (get('hour') >= (isNaN(cutoff) ? 14 : cutoff)) day += DAY;
      if (skip) while (weekend(day)) day += DAY;
      for (let i = 0; i < days; i++) {
        day += DAY;
        if (skip) while (weekend(day)) day += DAY;
      }

      return new Date(day).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }
  }

  customElements.define('ei-atc-sheet', EiAtcSheet);

  /* =====================================================================
     PDP sticky bar: bar reveal, stock Recharge widget sync (read-only),
     main form state. Opens the sheet.
     ===================================================================== */

  class EiStickyAtc extends HTMLElement {
    connectedCallback() {
      customElements.upgrade(this);
      this.bar = this.querySelector('[data-bar]');
      this.sheet = this.querySelector('ei-atc-sheet');
      this.productId = this.dataset.productId;
      this.mode = this.dataset.mode || 'single';
      this.pastMain = false;
      this.timers = new Set();
      this.mq = window.matchMedia(MOBILE);

      this.onMq = () => this.syncViewport();
      this.mq.addEventListener('change', this.onMq);

      this.onSectionEvent = (event) => this.handleSectionEvent(event);
      document.addEventListener('shopify:section:load', this.onSectionEvent);
      document.addEventListener('shopify:section:unload', this.onSectionEvent);

      this.onDocClick = (event) => {
        if (event.target.closest && event.target.closest('recharge-subscription-widget')) {
          later(this.timers, () => this.readPurchase('widget'), 150);
        }
      };
      if (this.mode !== 'monthly') document.addEventListener('click', this.onDocClick);

      this.ui = new AbortController();
      const {signal} = this.ui;
      this.querySelectorAll('[data-open], [data-open-cta]').forEach((el) => {
        el.addEventListener('click', () => this.open(el), {signal});
      });
      if (this.sheet) {
        this.sheet.addEventListener('ei-sheet:open', () => this.updateBar(), {signal});
        this.sheet.addEventListener('ei-sheet:close', () => this.updateBar(), {signal});
        this.sheet.addEventListener('ei-sheet:change', () => this.syncBar(), {signal});
        this.syncBar();
      }

      this.syncViewport();
    }

    disconnectedCallback() {
      this.mq.removeEventListener('change', this.onMq);
      document.removeEventListener('shopify:section:load', this.onSectionEvent);
      document.removeEventListener('shopify:section:unload', this.onSectionEvent);
      document.removeEventListener('click', this.onDocClick);
      this.ui.abort();
      this.teardownActive();
      this.timers.forEach((id) => clearTimeout(id));
      this.timers.clear();
    }

    /* ---------- viewport / main product binding ---------- */

    syncViewport() {
      if (this.mq.matches) {
        this.bindMain();
      } else {
        this.teardownActive();
      }
    }

    teardownActive() {
      if (this.sheet) this.sheet.teardown();
      this.unbindMain();
      this.pastMain = false;
      this.updateBar();
    }

    resolveForm() {
      return document.querySelector(
        `${PRODUCT_SECTION} form.product__form[data-product-form][data-product-id="${this.productId}"]`
      );
    }

    bindMain() {
      this.unbindMain();
      this.form = this.resolveForm();
      if (!this.form) {
        this.pastMain = false;
        this.updateBar();
        return;
      }

      const mainButton = this.form.querySelector('[data-add-to-cart]');
      if (mainButton && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          this.pastMain = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.updateBar();
        });
        this.io.observe(mainButton);
      }

      if (this.mode === 'monthly') return;

      const planInput = this.form.querySelector('[name="selling_plan"]');
      if (planInput && 'MutationObserver' in window) {
        this.mo = new MutationObserver(() => this.readPurchase('widget'));
        this.mo.observe(planInput, {attributes: true, attributeFilter: ['value']});
      }

      this.readPurchase('open');
      later(this.timers, () => this.readPurchase('open'), 1500);
    }

    unbindMain() {
      if (this.io) this.io.disconnect();
      if (this.mo) this.mo.disconnect();
      this.io = null;
      this.mo = null;
      this.form = null;
    }

    handleSectionEvent(event) {
      const target = event.target;
      const isProduct = target && target.querySelector && (target.matches(PRODUCT_SECTION) || target.querySelector(PRODUCT_SECTION));
      if (!isProduct) return;

      if (event.type === 'shopify:section:unload') {
        if (this.sheet && this.sheet.isOpen) this.sheet.close({skipFocus: true});
        this.unbindMain();
        this.pastMain = false;
        this.updateBar();
      } else if (this.mq.matches) {
        later(this.timers, () => this.bindMain(), 50);
      }
    }

    /* ---------- bar ---------- */

    updateBar() {
      if (!this.bar) return;
      const open = !!this.sheet && this.sheet.isOpen;
      const show = this.mq.matches && this.pastMain && !open && !!this.form;
      this.bar.classList.toggle('is-visible', show);
      this.bar.inert = !show;
      own(BODY_BAR, this, show);
    }

    // The bar price line follows the sheet's purchase option and selected plan.
    syncBar() {
      if (this.mode === 'monthly') return;
      this.dataset.purchase = this.sheet.purchase;
      const line = this.querySelector('[data-save-line]');
      if (line && this.sheet.savePercent !== undefined) {
        line.textContent = (line.dataset.template || '').replace('%%P%%', this.sheet.savePercent);
      }
    }

    /* ---------- stock widget (read-only) ---------- */

    widgetRadio(value) {
      const widget = document.querySelector('recharge-subscription-widget');
      return widget && widget.shadowRoot
        ? widget.shadowRoot.querySelector(`input[type="radio"][value="${value}"]`)
        : null;
    }

    readPurchase(source) {
      if (this.mode === 'monthly') return;
      const sheet = this.sheet;
      if (!sheet || !sheet.hasPlans || sheet.busy) return;
      if (source === 'widget') sheet.touched = false;
      else if (sheet.touched) return;

      const plans = this.form ? new FormData(this.form).getAll('selling_plan').filter(Boolean) : [];
      const radio = this.widgetRadio('subscription');

      if (plans.length) sheet.setPlan(plans[0]);

      // Widget not hydrated or not present: its state is unknown, keep the sheet's own choice.
      if (!radio && !plans.length) return;

      sheet.setPurchase(plans.length || (radio && radio.checked) ? 'subscription' : 'onetime');
    }

    open(opener) {
      if (!this.sheet || !this.mq.matches) return;
      if (this.mode === 'monthly') {
        let id;
        if (!this.sheet.tierSeeded) {
          const selected = this.form && this.form.querySelector('[name="id"]');
          id = selected && selected.value;
        }
        this.sheet.seedTier(id);
        this.sheet.open(opener);
        return;
      }
      this.readPurchase('open');
      const input = this.form && this.form.querySelector('[name="quantity"]');
      this.sheet.setQty(input ? input.value : 1);
      this.sheet.open(opener);
    }
  }

  customElements.define('ei-sticky-atc', EiStickyAtc);
})();
